const DEFAULT_FAVICON = "/favicon.png";

// Fetching the tenant's saved favicon costs a call to `dashboard/agent/settings`,
// which is gated behind `agent-options.index` — a permission most users do not
// hold just to render a tab icon. Caching the last-known URL here lets every
// page (and every other open window for this tenant, via the `storage` event)
// apply it without that call, while a browser that has never loaded Settings
// still falls back to the default until someone does.
export const FAVICON_STORAGE_KEY = "tenant-favicon-url";

/**
 * Swaps the app's <link rel="icon"> so the browser tab shows the tenant's
 * own favicon instead of the build-time default. Falls back to the default
 * whenever no tenant favicon is set (cleared, or none uploaded yet).
 */
export function applyFavicon(url) {
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url || DEFAULT_FAVICON;
}

/**
 * Caches the tenant's saved favicon URL. Pass only the backend's own
 * `favicon_url` (or a falsy value to clear it) — never a transient
 * `URL.createObjectURL` preview, which stops being valid the moment this tab
 * is closed and would otherwise poison every other window's cache.
 */
export function persistFavicon(url) {
  try {
    if (url) {
      localStorage.setItem(FAVICON_STORAGE_KEY, url);
    } else {
      localStorage.removeItem(FAVICON_STORAGE_KEY);
    }
  } catch {
    // Private-mode/quota failures just mean the tab keeps the default icon.
  }
}

/**
 * Applies whichever favicon was last cached for this tenant. Call once at
 * app boot so a window that opens directly on some other page — never having
 * mounted the Settings screen — still shows the tenant's favicon instead of
 * the default one.
 */
export function initFavicon() {
  let cached = null;
  try {
    cached = localStorage.getItem(FAVICON_STORAGE_KEY);
  } catch {
    // ignore — falls back to the default favicon below.
  }
  applyFavicon(cached);
}
